import React, { useMemo, useState } from 'react'
import { AppCheckBox } from '../AppFormFields/AppCheckBox/AppCheckBox.component'
import { Divider } from 'components/AppGoalCenter/AppGoalCenter.style'
import { AppToolTip } from '../AppToolTip/AppToolTip.component'
import { AppToolTipIcon } from '../AppToolTip/AppToolTipIcon'
import AppUpArrowIcon from '../AppSvgIcons/AppUpArrowIcon'
import AppDownArrowIcon from '../AppSvgIcons/AppDownArrowIcon'
import { AppTextField } from '../AppFormFields/AppTextField/AppTextField.component'
import { ReactComponent as SearchIcon } from 'assets/svgs/magnify.svg'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { getInitials } from 'components/Chat/helper'
import { AppButton } from '../AppButton/AppButton.component'

type FilterOption = {
  label: string
  value: string | number
  toolTipText?: string
  disabled?: boolean
  hideOption?: boolean
  // Optional extra fields (e.g., for assignees)
  user?: {
    id: string
    firstName: string
    lastName: string
  }
}

export type FilterCategory = {
  title: string
  subTitle?: string
  label: string
  toolTipText?: string
  options: FilterOption[]
  disabled?: boolean
}

type FiltersProps = {
  value: Record<string, (string | number)[]>
  categories: FilterCategory[]
  onFilterChange: (
    selectedFilters: Record<string, (string | number)[]>,
    filterCount: number
  ) => void
  showSearchBox?: boolean
}

export const AppFilterMenu: React.FC<FiltersProps> = ({
  value,
  categories,
  onFilterChange,
  showSearchBox,
}) => {
  // Applied filters (what's currently active)
  const [appliedFilters, setAppliedFilters] = useState<Record<string, (string | number)[]>>(value)
  // Pending filters (what user is selecting but hasn't applied yet)
  const [pendingFilters, setPendingFilters] = useState<Record<string, (string | number)[]>>(value)
  const [isCategoryOpen, setIsCategoryOpen] = useState<Record<string, boolean>>(
    categories.reduce((acc, category) => ({ ...acc, [category.label]: false }), {})
  )

  // per-category search terms (and optional legacy top search for business)
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({})
  const [legacyBusinessSearch, setLegacyBusinessSearch] = useState<string>('')

  const areAllOpen = useMemo(() => Object.values(isCategoryOpen).every(Boolean), [isCategoryOpen])

  const getFilterCount = (filters: Record<string, any>): number => {
    return Object.values(filters).reduce((count, values) => {
      return Array.isArray(values) ? count + values.length : count
    }, 0)
  }

  const applyFilters = () => {
    setAppliedFilters(pendingFilters)
    onFilterChange(pendingFilters, getFilterCount(pendingFilters))
  }

  const handleOptionToggle = (category: string, value: string | number) => {
    setPendingFilters((prev) => {
      const currentValues = prev[category] || []
      const updatedValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value]
      return { ...prev, [category]: updatedValues }
    })
  }

  const clearAllFilters = () => {
    const reset: Record<string, (string | number)[]> = {}
    categories.forEach((c) => {
      reset[c.label] = []
    })
    setSearchQueries({})
    setLegacyBusinessSearch('')
    setPendingFilters(reset)
    setAppliedFilters(reset)
    onFilterChange(reset, getFilterCount(reset))
  }

  const expandOrCollapseAll = () => {
    const next = categories.reduce<Record<string, boolean>>(
      (acc, c) => ({ ...acc, [c.label]: !areAllOpen }),
      {}
    )
    setIsCategoryOpen(next)
  }

  const toggleCategory = (categoryLabel: string) => {
    setIsCategoryOpen((prev) => ({
      ...prev,
      [categoryLabel]: !prev[categoryLabel],
    }))
  }

  const selectAllInCategory = (category: FilterCategory) => {
    const allValues = category.options
      .filter((o) => !o.hideOption && !o.disabled)
      .map((o) => o.value)
    setPendingFilters((prev) => ({ ...prev, [category.label]: allValues }))
  }

  const clearCategory = (category: FilterCategory) => {
    setPendingFilters((prev) => ({ ...prev, [category.label]: [] }))
  }

  const setCategorySearch = (label: string, val: string) => {
    setSearchQueries((prev) => ({ ...prev, [label]: val }))
  }

  const showCategorySearch = (category: FilterCategory) => {
    // Show for specific categories per design
    return ['goalId', 'assigneeId', 'businessName'].includes(category.label)
  }

  const getFilteredOptions = (category: FilterCategory) => {
    const term =
      (searchQueries[category.label] ?? '').trim() ||
      (category.label === 'businessName' && showSearchBox ? legacyBusinessSearch : '')

    if (term) {
      const lower = term.toLowerCase()
      return category.options.filter((option) => option.label.toLowerCase().includes(lower))
    }
    return category.options
  }

  // Check if there are pending changes
  const hasPendingChanges = useMemo(() => {
    return JSON.stringify(appliedFilters) !== JSON.stringify(pendingFilters)
  }, [appliedFilters, pendingFilters])

  return (
    <div className="p-4 bg-app-white-100 rounded-lg max-h-[75vh] overflow-auto shadow w-[360px] border border-app-grey-40 flex flex-col">
      <div className="mb-4">
        <h3 className="text-lg text-app-black-100 font-semibold font-inter">Filters</h3>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4 text-app-grey-80 text-sm">
            <button
              type="button"
              onClick={expandOrCollapseAll}
              className="hover:text-app-black-100"
            >
              {areAllOpen ? 'Collapse All' : 'Expand All'}
            </button>
            <button type="button" onClick={clearAllFilters} className="hover:text-app-black-100">
              Clear All
            </button>
          </div>
          <div>
            <AppButton
              label="Apply Filters"
              variant="primary"
              size="md"
              onClick={applyFilters}
              disabled={!hasPendingChanges}
              isFullWidth
            />
          </div>
        </div>
      </div>

      {showSearchBox && (
        <div className="mb-4">
          <AppTextField
            size="md"
            placeholder="Search business"
            value={legacyBusinessSearch}
            onChange={(e) => setLegacyBusinessSearch(e.target.value)}
            Icon={SearchIcon}
          />
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {categories.map((category, index) => {
          const isOpen = isCategoryOpen[category.label]
          const filteredOptions = getFilteredOptions(category)

          return (
            <div key={category.title} className="mb-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <h4 className="text-base text-app-black-100 font-inter font-medium">
                    {category.title}
                  </h4>
                  {category.toolTipText && (
                    <AppToolTip name="ToolTip" text={category.toolTipText} variant="primary">
                      <AppToolTipIcon />
                    </AppToolTip>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {isOpen && (
                    <>
                      <button
                        type="button"
                        onClick={() => selectAllInCategory(category)}
                        className="text-sm text-app-primary-90 hover:underline"
                      >
                        All
                      </button>
                      <button
                        type="button"
                        onClick={() => clearCategory(category)}
                        className="text-sm text-app-primary-90 hover:underline"
                      >
                        None
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => toggleCategory(category.label)}
                    className="focus:outline-none"
                    aria-label={`Toggle ${category.title}`}
                  >
                    {isOpen ? <AppUpArrowIcon /> : <AppDownArrowIcon />}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="space-y-4">
                  {showCategorySearch(category) && (
                    <div className="mb-4">
                      <AppTextField
                        size="sm"
                        placeholder={
                          category.label === 'goalId'
                            ? 'Search goals...'
                            : category.label === 'assigneeId'
                            ? 'Search assignee...'
                            : 'Search business...'
                        }
                        value={searchQueries[category.label] ?? ''}
                        onChange={(e) => setCategorySearch(category.label, e.target.value)}
                        Icon={SearchIcon}
                      />
                    </div>
                  )}

                  {filteredOptions.map((option) => {
                    if (option.hideOption) return null

                    const checkboxId = `${category.label}-${option.value}`
                    const checked = pendingFilters[category.label]?.includes(option.value) || false

                    const isAssignee = category.label === 'assigneeId' && option.user

                    return (
                      <div className="space-y-4" key={option.value}>
                        <div className="flex items-center">
                          <div className="w-[10%]">
                            <AppCheckBox
                              id={checkboxId}
                              value={checked}
                              onChange={() => handleOptionToggle(category.label, option.value)}
                              disabled={category.disabled || option.disabled}
                            />
                          </div>
                          <div className="w-[90%]">
                            {isAssignee ? (
                              <label
                                htmlFor={checkboxId}
                                className="flex items-center gap-2 cursor-pointer"
                              >
                                <AppAvatar
                                  size="xs"
                                  type="mixed"
                                  shape="Rounded"
                                  text={getInitials({
                                    firstName: option.user?.firstName || '',
                                    lastName: option.user?.lastName || '',
                                  })}
                                  textStyles="text-[10px]"
                                />
                                <span className="text-black text-base">{option.label}</span>
                              </label>
                            ) : (
                              <label
                                htmlFor={checkboxId}
                                className="text-black text-base cursor-pointer flex-1"
                              >
                                {option.label}
                              </label>
                            )}
                            {option.toolTipText && (
                              <AppToolTip
                                name="ToolTip"
                                text={option.toolTipText}
                                variant="primary"
                              >
                                <AppToolTipIcon />
                              </AppToolTip>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              <div className="mt-4">{categories.length !== index + 1 && <Divider />}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
